import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { COPIAS_MAXIMAS } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Botao, Campo, Falha, Girando, Selecao, Vazio, cx } from "../../components/ui.js";
import { MolduraDoPainel, Nota } from "./MolduraDoPainel.js";
import { EstadoDaImpressora } from "./EstadoDaImpressora.js";
import { CartaoDoTrabalho } from "./FilaDeImpressao.js";
import {
  confirmacaoDoAlvo,
  enviarAlvo,
  fotosDoAlvo,
  motivoDeFolhaVazia,
  subtituloDoPainel,
  trabalhoRecemEnviado,
  type AlvoDeImpressao,
} from "./alvo.js";
import { estaAberto, fraseDoPainelEnviado, type TrabalhoDaFila } from "./fila.js";
import {
  OPCOES_DE_COPIAS,
  estadoDesatualizado,
  folhasPorExtenso,
  lerImpressora,
  limitarCopias,
  mensagemDoErro,
  podeImprimir,
  rotuloDaImpressora,
} from "./impressora.js";
import {
  useAgora,
  useAnuncioDaFila,
  useCancelarTrabalho,
  useTrabalhosDeImpressao,
} from "./useFilaDeImpressao.js";

/** De onde o administrador sem impressora cadastra uma. */
export type CadastroDeImpressora =
  /** O painel de impressoras mora na própria tela: abre-se ali mesmo. */
  | { tipo: "painel"; aoAbrir: () => void }
  /** Mora noutra tela: um link até ela, sem duplicar o painel. */
  | { tipo: "pagina"; para: string };

/**
 * "Imprimir na impressora": escolhe a impressora, confere o estado dela, escolhe as cópias e confirma.
 * Depois de enviar, mostra o trabalho entrando na fila.
 *
 * Serve à folha montada na tela de Impressão e à folha de um conjunto de marcadores (`alvo`): o que muda
 * entre as duas — textos, aviso, chamada ao servidor — está em `alvo.ts`; aqui fica o que é igual.
 *
 * Imprimir gasta papel e fita, e não tem volta — por isso o painel termina numa frase de confirmação
 * ("Vai imprimir 2 folhas, 4 por folha, em Sala") e o botão repete a contagem. E por isso a tela nunca
 * diz que a folha saiu: ela diz que foi **enviada**, e o "Concluído" só aparece quando o servidor o diz.
 */
export function PainelImprimir({
  alvo,
  ehAdmin,
  podeCompartilhar,
  aoFechar,
  aoBaixar,
  aoCompartilhar,
  cadastro,
}: {
  alvo: AlvoDeImpressao;
  ehAdmin: boolean;
  podeCompartilhar: boolean;
  aoFechar: () => void;
  aoBaixar: () => void;
  aoCompartilhar: () => void;
  /** O administrador sem impressora cadastrada vai daqui ao cadastro. */
  cadastro: CadastroDeImpressora;
}) {
  const utils = trpc.useUtils();
  const agora = useAgora();

  // Ao abrir, uma lista nova: o estado guardado de uma impressora vale pouco, e a lista pode ter
  // mudado desde a última vez (o admin cadastrou uma, ou outra aba a testou).
  const impressoras = trpc.impressao.impressoras.useQuery(undefined, {
    staleTime: 0,
    refetchOnMount: "always",
  });
  const lista = impressoras.data ?? [];

  const [escolhidaId, setEscolhidaId] = useState<number | null>(null);
  // Sem escolha, a primeira da lista (que o servidor ordena por nome): quem tem uma só não escolhe nada.
  const escolhida = lista.find((i) => i.id === escolhidaId) ?? lista[0] ?? null;
  const mostrarDono = ehAdmin && new Set(lista.map((i) => i.donoId)).size > 1;

  const [copias, setCopias] = useState(1);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState<TrabalhoDaFila | null>(null);

  // ─── Verificar a impressora ───────────────────────────────────────────────

  // Direto pelo cliente, e não por `useMutation`: a verificação automática dispara ao montar, e o
  // `StrictMode` (que desmonta e remonta os efeitos em desenvolvimento) faz o observador da mutação
  // largar o trabalho em andamento — ele ficaria "pendente" para sempre, mesmo com a resposta chegada.
  // O resultado fica gravado no servidor; a lista é que o traz de volta, igual para todo mundo.
  const [verificandoIds, setVerificandoIds] = useState<number[]>([]);
  const verificando = escolhida ? verificandoIds.includes(escolhida.id) : false;

  async function verificar(id: number) {
    setVerificandoIds((ids) => [...ids, id]);
    try {
      // Nunca lança por rede (a resposta já diz "sem contato"); só por sessão, permissão ou queda de internet.
      await utils.client.impressao.testarImpressora.mutate({ id });
    } catch (e) {
      setErro(mensagemDoErro(e, "Não foi possível verificar a impressora."));
    } finally {
      setVerificandoIds((ids) => ids.filter((i) => i !== id));
      void utils.impressao.impressoras.invalidate();
    }
  }

  // Pergunta sozinho, uma vez por impressora, se o que se sabe dela é velho ou não existe: o operador
  // abre o painel para imprimir, e não para descobrir que precisava apertar "Verificar".
  const jaVerificadas = useRef(new Set<number>());
  useEffect(() => {
    if (!escolhida || jaVerificadas.current.has(escolhida.id)) return;
    jaVerificadas.current.add(escolhida.id);
    if (estadoDesatualizado(escolhida.estadoEm, new Date())) void verificar(escolhida.id);
    // Só quando a impressora escolhida muda (ou chega a primeira).
  }, [escolhida?.id]);

  // ─── Enviar ───────────────────────────────────────────────────────────────

  const fotosNaFolha = fotosDoAlvo(alvo);
  // Enquanto pergunta à impressora, o botão espera: o que se sabia dela é justamente o que está sendo
  // conferido, e "pode" ou "não pode" agora seria um palpite que some em instantes.
  const decisao = podeImprimir({
    impressora: escolhida,
    fotosNaFolha,
    enviando: verificando,
    motivoSeVazia: motivoDeFolhaVazia(alvo),
  });

  const imprimir = useMutation({
    mutationFn: (destino: { impressoraId: number; copias: number }) =>
      enviarAlvo(utils.client, alvo, destino),
    onSuccess: (r) => {
      // Enquanto a fila de verdade não volta, o trabalho aparece com o que se sabe: foi aceito, aguarda.
      setEnviado(trabalhoRecemEnviado(alvo, r, escolhida?.nome ?? "", new Date()));
      void utils.impressao.trabalhos.invalidate();
    },
    onError: (e) => setErro(mensagemDoErro(e, "Não foi possível enviar a folha. Tente de novo.")),
  });

  function enviar() {
    if (!escolhida || !decisao.pode || imprimir.isPending) return;
    setErro(null);
    imprimir.mutate({ impressoraId: escolhida.id, copias });
  }

  const trabalhos = useTrabalhosDeImpressao();
  const anuncio = useAnuncioDaFila(trabalhos.data, enviado?.id);
  const { cancelar, cancelandoId, erro: erroDoCancelar } = useCancelarTrabalho();

  // ─── O que mostrar ────────────────────────────────────────────────────────

  const subtitulo = subtituloDoPainel(alvo);
  const bloqueada = !!escolhida && !decisao.pode && decisao.motivo !== null;
  const confirmacao = escolhida
    ? confirmacaoDoAlvo(alvo, copias, rotuloDaImpressora(escolhida, false))
    : null;

  // Depois de enviado: o painel só acompanha aquele trabalho.
  if (enviado) {
    const atual = trabalhos.data?.find((t) => t.id === enviado.id) ?? enviado;
    return (
      <MolduraDoPainel
        titulo="Folha enviada"
        subtitulo={subtitulo}
        aoFechar={aoFechar}
        rodape={
          <div className="flex flex-wrap gap-2 pb-1">
            <Botao tom="brasa" className="min-h-11 flex-1 sm:flex-none" onClick={aoFechar}>
              Fechar
            </Botao>
          </div>
        }
      >
        <p className="text-[1rem] leading-relaxed text-texto">
          {fraseDoPainelEnviado(atual.estado)}
        </p>
        <ul>
          <CartaoDoTrabalho
            trabalho={atual}
            agora={agora}
            cancelando={cancelandoId === atual.id}
            erro={erroDoCancelar?.id === atual.id ? erroDoCancelar.mensagem : null}
            aoCancelar={cancelar}
          />
        </ul>
        {estaAberto(atual) && (
          <p className="text-[0.8125rem] leading-relaxed text-texto-suave">
            A folha só conta como impressa quando o estado mudar para “Concluído”.
          </p>
        )}
        <p role="status" aria-live="polite" className="sr-only">
          {anuncio}
        </p>
      </MolduraDoPainel>
    );
  }

  const outraForma = (
    <section
      aria-label="Outra forma de imprimir"
      className={cx(
        "rounded-peca border px-4 py-3",
        bloqueada || lista.length === 0 ? "border-brasa/40" : "border-borda",
      )}
    >
      <p className="text-[0.9375rem] text-texto">Prefere imprimir pelo app da Canon?</p>
      <p className="mt-0.5 text-[0.8125rem] leading-relaxed text-texto-suave">
        Baixe o JPEG da folha ou compartilhe com o app. Funciona mesmo quando o sistema não alcança
        a impressora.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Botao tom="quieto" className="min-h-11" onClick={aoBaixar}>
          Baixar JPEG
        </Botao>
        {podeCompartilhar && (
          <Botao tom="quieto" className="min-h-11" onClick={aoCompartilhar}>
            Compartilhar com o app
          </Botao>
        )}
      </div>
    </section>
  );

  const rodapeSemImpressora = (
    <div className="flex flex-wrap gap-2 pb-1">
      <Botao tom="quieto" className="min-h-11" onClick={aoFechar}>
        Fechar
      </Botao>
    </div>
  );

  return (
    <MolduraDoPainel
      titulo="Imprimir na impressora"
      subtitulo={subtitulo}
      aoFechar={aoFechar}
      rodape={
        escolhida ? (
          <div className="flex flex-wrap gap-2 pb-1">
            {/* Sem nada a imprimir o botão não espera a impressora: o motivo (acima) é outro. */}
            {bloqueada && fotosNaFolha > 0 && (
              <p className="w-full text-[0.8125rem] leading-snug text-texto-suave">
                O botão libera quando a impressora estiver pronta.
              </p>
            )}
            <Botao
              tom="brasa"
              className="min-h-11 flex-1 sm:flex-none"
              disabled={!decisao.pode}
              carregando={imprimir.isPending}
              aria-describedby={bloqueada ? "motivo-de-nao-imprimir" : undefined}
              onClick={enviar}
            >
              Imprimir {folhasPorExtenso(copias)}
            </Botao>
            <Botao tom="quieto" className="min-h-11" onClick={aoFechar}>
              Cancelar
            </Botao>
          </div>
        ) : (
          rodapeSemImpressora
        )
      }
    >
      {impressoras.isLoading ? (
        <Girando className="mx-auto my-8 size-6 text-texto-fraco" />
      ) : impressoras.isError && !impressoras.data ? (
        <Falha
          mensagem={mensagemDoErro(impressoras.error, "Não foi possível ver as impressoras.")}
          tentarNovamente={() => void impressoras.refetch()}
        />
      ) : !escolhida ? (
        <>
          {/* O `Vazio` é feito para a página inteira; num painel, o respiro dele sobra. */}
          <div className="-my-5">
            <Vazio
              titulo="Nenhuma impressora cadastrada"
              descricao={
                !ehAdmin
                  ? "Peça ao administrador para cadastrar a impressora da casa. Enquanto isso, baixe a folha ou compartilhe com o app da Canon."
                  : cadastro.tipo === "painel"
                    ? "Cadastre a impressora para o sistema enviar a folha direto, sem passar pelo app da Canon."
                    : "Cadastre a impressora em Impressão → Impressoras e volte aqui: o sistema envia a folha direto, sem passar pelo app da Canon."
              }
              acao={
                !ehAdmin ? undefined : cadastro.tipo === "painel" ? (
                  <Botao tom="brasa" className="min-h-11" onClick={cadastro.aoAbrir}>
                    Cadastrar impressora
                  </Botao>
                ) : (
                  // Um link de verdade, e não um botão que navega: o leitor de tela o anuncia como link, e
                  // dá para abri-lo noutra aba.
                  <Link
                    to={cadastro.para}
                    className="inline-flex min-h-11 items-center justify-center rounded-peca bg-brasa px-4 py-3 text-[0.9375rem] leading-none font-semibold tracking-[-0.01em] text-fundo transition-colors hover:bg-brasa-forte active:bg-brasa-forte"
                  >
                    Abrir Impressão → Impressoras
                  </Link>
                )
              }
            />
          </div>
          {outraForma}
        </>
      ) : (
        <>
          <section aria-label="Impressora" className="flex flex-col gap-3">
            {lista.length > 1 ? (
              <Campo rotulo="Impressora">
                <Selecao
                  value={escolhida.id}
                  onChange={(e) => {
                    setEscolhidaId(Number(e.target.value));
                    setErro(null);
                  }}
                >
                  {lista.map((i) => (
                    <option key={i.id} value={i.id}>
                      {rotuloDaImpressora(i, mostrarDono)}
                    </option>
                  ))}
                </Selecao>
              </Campo>
            ) : (
              <div>
                <p className="text-[0.8125rem] font-medium text-texto-suave">Impressora</p>
                <p className="mt-0.5 text-[1.0625rem] text-texto wrap-anywhere">
                  {rotuloDaImpressora(escolhida, mostrarDono)}
                </p>
              </div>
            )}

            <div className="flex flex-col items-start gap-3 rounded-peca border border-borda bg-superficie px-4 py-3">
              <EstadoDaImpressora
                estado={escolhida.estado}
                estadoEm={escolhida.estadoEm}
                conexao={escolhida.conexao}
                agente={escolhida.agente}
                verificando={verificando}
                agora={agora}
              />
              <Botao
                tom="quieto"
                className="min-h-11"
                carregando={verificando}
                onClick={() => void verificar(escolhida.id)}
              >
                Verificar agora
              </Botao>
            </div>
          </section>

          {/* O porquê fica colado ao estado; e, sem poder imprimir por aqui, o caminho que funciona sobe
              para logo abaixo dele, em vez de ficar no fim de uma rolagem. */}
          {decisao.motivo && (
            <Nota tom="impedimento" id="motivo-de-nao-imprimir">
              {decisao.motivo}
            </Nota>
          )}
          {decisao.aviso && lerImpressora(escolhida).situacao !== "pronta" && (
            <Nota tom="aviso">{decisao.aviso}</Nota>
          )}
          {bloqueada && outraForma}

          <fieldset>
            <legend className="mb-1.5 text-[0.8125rem] font-medium text-texto-suave">Cópias</legend>
            <div className="flex gap-2">
              {OPCOES_DE_COPIAS.map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={copias === n}
                  aria-label={`${n} ${n === 1 ? "cópia" : "cópias"}`}
                  onClick={() => setCopias(limitarCopias(n))}
                  className={cx(
                    "num size-12 rounded-peca border text-[1.0625rem] font-medium transition-colors",
                    copias === n
                      ? "border-brasa bg-brasa-fraca text-brasa"
                      : "border-borda bg-superficie text-texto-suave hover:border-borda-forte",
                  )}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[0.8125rem] text-texto-suave">
              Cada cópia sai numa folha. O máximo por vez é {COPIAS_MAXIMAS}.
            </p>
          </fieldset>

          {/* Sobre a folha, e não sobre a impressora: fica junto da confirmação, onde o operador decide. */}
          {confirmacao?.aviso && <Nota tom="aviso">{confirmacao.aviso}</Nota>}

          {confirmacao && (
            <div className="rounded-peca border border-borda-forte bg-superficie-alta px-4 py-3">
              <p className="text-[1rem] leading-snug text-texto">{confirmacao.frase}</p>
              <ul className="mt-2 flex flex-col gap-1 text-[0.8125rem] leading-relaxed text-texto-suave">
                {confirmacao.notas.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
          )}

          {erro && <Falha mensagem={erro} />}

          {!bloqueada && outraForma}
        </>
      )}
    </MolduraDoPainel>
  );
}
